/**
 * End-to-end encryption helpers for hacking.community chat.
 *
 * Scheme:
 *   - Each user owns an ECDH P-256 keypair. The PRIVATE key never leaves the browser
 *     (localStorage under hc_priv_<user_id>). The PUBLIC key is uploaded to the server.
 *   - For each message, the sender derives a shared secret via ECDH(my_priv, their_pub)
 *     and encrypts the plaintext with AES-GCM-256 + a fresh 12-byte IV.
 *   - The server only stores ciphertext_b64 + iv_b64 — it cannot decrypt.
 *
 * UX safeguards:
 *   - "Export key" button writes the private JWK to a downloadable .json file.
 *   - "Import key" accepts that file back when the user moves device/browser.
 *   - If a private key is missing on this device, an "Unlock chat" prompt asks the user
 *     to either upload their backup or accept that prior messages are unreadable.
 */

const PRIV_KEY = (uid) => `hc_priv_${uid}`;
const PUB_KEY = (uid) => `hc_pub_${uid}`;

function b64(buf) {
    let s = "";
    const bytes = new Uint8Array(buf);
    for (const b of bytes) s += String.fromCharCode(b);
    return btoa(s);
}

function unb64(s) {
    const bin = atob(s);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out.buffer;
}

/** Generate a brand new ECDH P-256 keypair and persist both halves locally. */
export async function generateKeyPair(userId) {
    const kp = await crypto.subtle.generateKey(
        { name: "ECDH", namedCurve: "P-256" },
        true, // extractable so we can export the private key for backup
        ["deriveKey", "deriveBits"],
    );
    const privJwk = await crypto.subtle.exportKey("jwk", kp.privateKey);
    const pubJwk = await crypto.subtle.exportKey("jwk", kp.publicKey);
    localStorage.setItem(PRIV_KEY(userId), JSON.stringify(privJwk));
    localStorage.setItem(PUB_KEY(userId), JSON.stringify(pubJwk));
    return { privJwk, pubJwk };
}

export function getLocalPrivateJwk(userId) {
    const s = localStorage.getItem(PRIV_KEY(userId));
    return s ? JSON.parse(s) : null;
}

export function getLocalPublicJwk(userId) {
    const s = localStorage.getItem(PUB_KEY(userId));
    return s ? JSON.parse(s) : null;
}

/** Hex SHA-256 fingerprint of a public JWK, for trust-on-first-use display. */
export async function fingerprint(jwk) {
    const json = JSON.stringify(jwk);
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(json));
    return Array.from(new Uint8Array(digest))
        .slice(0, 16)
        .map((b) => b.toString(16).padStart(2, "0"))
        .join(":");
}

async function importPublic(jwk) {
    return crypto.subtle.importKey("jwk", jwk, { name: "ECDH", namedCurve: "P-256" }, false, []);
}

async function importPrivate(jwk) {
    return crypto.subtle.importKey(
        "jwk", jwk, { name: "ECDH", namedCurve: "P-256" }, false, ["deriveKey", "deriveBits"],
    );
}

async function deriveAesKey(myPrivJwk, theirPubJwk) {
    const myPriv = await importPrivate(myPrivJwk);
    const theirPub = await importPublic(theirPubJwk);
    return crypto.subtle.deriveKey(
        { name: "ECDH", public: theirPub },
        myPriv,
        { name: "AES-GCM", length: 256 },
        false,
        ["encrypt", "decrypt"],
    );
}

/** Encrypt a UTF-8 string for a recipient. Returns base64 ciphertext + iv. */
export async function encryptMessage({ plaintext, myPrivJwk, theirPubJwk }) {
    const aes = await deriveAesKey(myPrivJwk, theirPubJwk);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        aes,
        new TextEncoder().encode(plaintext),
    );
    return { ciphertext: b64(ct), iv: b64(iv) };
}

/**
 * For group chats: produce one envelope per recipient + a self-copy for the sender.
 * Returns:
 *   { ciphertext, iv }                        — sender's own self-encrypted copy
 *   recipient_ciphertexts: { user_id: {ciphertext, iv} }   — per-recipient envelopes
 */
export async function encryptGroupMessage({ plaintext, myPrivJwk, recipients }) {
    // Self-copy uses sender's own public key (echo to themselves so they can read history)
    const myPubFromPriv = { kty: "EC", crv: "P-256", x: myPrivJwk.x, y: myPrivJwk.y };
    const self = await encryptMessage({ plaintext, myPrivJwk, theirPubJwk: myPubFromPriv });
    const recipient_ciphertexts = {};
    for (const r of recipients) {
        const env = await encryptMessage({ plaintext, myPrivJwk, theirPubJwk: r.pubJwk });
        recipient_ciphertexts[r.user_id] = env;
    }
    return { ...self, recipient_ciphertexts };
}

/** Decrypt a base64 ciphertext+iv pair to the plaintext UTF-8 string. */
export async function decryptMessage({ ciphertext, iv, myPrivJwk, theirPubJwk }) {
    try {
        const aes = await deriveAesKey(myPrivJwk, theirPubJwk);
        const pt = await crypto.subtle.decrypt(
            { name: "AES-GCM", iv: unb64(iv) },
            aes,
            unb64(ciphertext),
        );
        return new TextDecoder().decode(pt);
    } catch {
        return null; // tampered / wrong key / different sender keypair version
    }
}

/** Trigger a file download of the user's private key for backup. */
export function exportPrivateKeyFile(userId) {
    const priv = getLocalPrivateJwk(userId);
    if (!priv) throw new Error("No private key on this device");
    const blob = new Blob([JSON.stringify(priv, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hacking-community-key-${userId}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

/** Replace the local private key with one restored from a backup file. */
export async function importPrivateKeyFile(userId, file) {
    const text = await file.text();
    const jwk = JSON.parse(text);
    if (jwk.kty !== "EC" || jwk.crv !== "P-256" || !jwk.d) {
        throw new Error("Not a valid hacking.community private key file");
    }
    // Sanity-check by exporting the matching public JWK
    const priv = await importPrivate(jwk);
    const pub = { kty: "EC", crv: "P-256", x: jwk.x, y: jwk.y };
    localStorage.setItem(PRIV_KEY(userId), JSON.stringify(jwk));
    localStorage.setItem(PUB_KEY(userId), JSON.stringify(pub));
    return { priv, pub };
}

// ─────────────────────────────────────────────────────────────────────────
// iter167k — Password-encrypted private-key bundle (Signal/WhatsApp grade).
//
// Goal: a user should never see the "older messages can't be decrypted on
// this device" banner just because they signed in on a new browser. We
// achieve this by storing the private JWK on the server, encrypted with
// AES-GCM using a key derived from the user's password via PBKDF2. The
// server only stores opaque ciphertext + iv + salt — it cannot decrypt.
//
// Flow (called once during login while the password is still in scope):
//   • POST /api/users/me/key-bundle   { ciphertext, iv, salt, v, public_jwk }
//   • GET  /api/users/me/key-bundle  → returns the encrypted blob if any
//
// On new device: GET the blob, decrypt with the password, save locally.
// On every login: also re-upload to keep the bundle in sync if rotation
// ever happens.
//
// PBKDF2 params: SHA-256, 200,000 iters, 16-byte random salt. AES-GCM-256
// with a fresh 12-byte IV every encryption.
// ─────────────────────────────────────────────────────────────────────────
const KB_VERSION = 1;
const KB_PBKDF2_ITERS = 200_000;

async function passwordToAesKey(password, salt) {
    const baseKey = await crypto.subtle.importKey(
        "raw",
        new TextEncoder().encode(password),
        { name: "PBKDF2" },
        false,
        ["deriveKey"],
    );
    return crypto.subtle.deriveKey(
        { name: "PBKDF2", salt, iterations: KB_PBKDF2_ITERS, hash: "SHA-256" },
        baseKey,
        { name: "AES-GCM", length: 256 },
        false,
        ["encrypt", "decrypt"],
    );
}

/** Encrypt the current local private JWK with the user's password. Returns
 *  the payload to POST to /api/users/me/key-bundle. Throws if no key yet. */
export async function buildEncryptedKeyBundle(userId, password) {
    const priv = getLocalPrivateJwk(userId);
    const pub = getLocalPublicJwk(userId);
    if (!priv || !pub) throw new Error("No local key to back up");
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const aes = await passwordToAesKey(password, salt);
    const ct = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        aes,
        new TextEncoder().encode(JSON.stringify(priv)),
    );
    return {
        v: KB_VERSION,
        ciphertext: b64(ct),
        iv: b64(iv.buffer),
        salt: b64(salt.buffer),
        public_jwk: pub,
    };
}

/** Decrypt an encrypted bundle (from GET /api/users/me/key-bundle) and
 *  install the recovered private JWK into localStorage. */
export async function restoreEncryptedKeyBundle(userId, bundle, password) {
    if (!bundle || !bundle.ciphertext) throw new Error("Empty bundle");
    if ((bundle.v || 1) !== KB_VERSION) throw new Error("Unsupported bundle version");
    const salt = new Uint8Array(unb64(bundle.salt));
    const iv = new Uint8Array(unb64(bundle.iv));
    const aes = await passwordToAesKey(password, salt);
    let pt;
    try {
        pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, aes, unb64(bundle.ciphertext));
    } catch {
        throw new Error("Wrong password — couldn't unlock the encrypted-chat backup");
    }
    const privJwk = JSON.parse(new TextDecoder().decode(pt));
    if (privJwk.kty !== "EC" || privJwk.crv !== "P-256" || !privJwk.d) {
        throw new Error("Restored bundle isn't a P-256 ECDH key");
    }
    const pubJwk = bundle.public_jwk || { kty: "EC", crv: "P-256", x: privJwk.x, y: privJwk.y };
    localStorage.setItem(PRIV_KEY(userId), JSON.stringify(privJwk));
    localStorage.setItem(PUB_KEY(userId), JSON.stringify(pubJwk));
    return { privJwk, pubJwk };
}


/**
 * Encrypt a raw byte buffer (e.g. an image File's contents) for a recipient.
 * Same ECDH+AES-GCM scheme as text messages, just operates on the raw
 * ArrayBuffer instead of a UTF-8 string.
 *
 * Returns { ciphertext_b64, iv_b64 }.
 */
export async function encryptBlob({ bytes, myPrivJwk, theirPubJwk }) {
    const aes = await deriveAesKey(myPrivJwk, theirPubJwk);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        aes,
        bytes,
    );
    return { ciphertext_b64: b64(ct), iv_b64: b64(iv) };
}

/**
 * Decrypt a ciphertext blob back to a Uint8Array — call URL.createObjectURL
 * on a Blob([bytes]) to render it as <img src> without ever touching disk.
 *
 * Returns null on integrity failure (wrong key, tampered, etc.).
 */
export async function decryptBlobToBytes({ ciphertext_b64, iv_b64, myPrivJwk, theirPubJwk }) {
    try {
        const aes = await deriveAesKey(myPrivJwk, theirPubJwk);
        const pt = await crypto.subtle.decrypt(
            { name: "AES-GCM", iv: unb64(iv_b64) },
            aes,
            unb64(ciphertext_b64),
        );
        return new Uint8Array(pt);
    } catch {
        return null;
    }
}

// ============================================================
// Group attachments — ephemeral-key wrapping protocol
// ============================================================
// 1:1 attachments use the ECDH-derived AES key directly. Groups can't do
// that cheaply (we'd re-encrypt the blob N times). Instead:
//   sender picks a fresh K → AES-encrypts blob with K → wraps K to each
//   recipient via their ECDH-derived Kw_i.
// Each recipient unwraps K with their derived Kw_i, then decrypts the blob.

/**
 * Encrypt raw bytes with a freshly-generated ephemeral AES-GCM-256 key.
 * Returns the ciphertext + iv + raw_key (so the caller can wrap it
 * per-recipient).
 */
export async function encryptBlobWithFreshKey(bytes) {
    const rawKey = crypto.getRandomValues(new Uint8Array(32));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const aes = await crypto.subtle.importKey(
        "raw", rawKey, { name: "AES-GCM" }, false, ["encrypt"],
    );
    const ct = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        aes,
        bytes,
    );
    return {
        ciphertext_b64: b64(ct),
        iv_b64: b64(iv),
        raw_key_bytes: rawKey,   // keep around to wrap to recipients
    };
}

/**
 * Wrap an ephemeral 32-byte symmetric key K for a single recipient using
 * ECDH(my_priv, their_pub). Returns {key_ct_b64, key_iv_b64}.
 */
export async function wrapKeyForRecipient({ rawKey, myPrivJwk, theirPubJwk }) {
    const aes = await deriveAesKey(myPrivJwk, theirPubJwk);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        aes,
        rawKey,
    );
    return { key_ct_b64: b64(ct), key_iv_b64: b64(iv) };
}

/**
 * Unwrap an ephemeral symmetric key envelope into raw bytes.
 * Returns Uint8Array(32) or null on tag failure.
 */
export async function unwrapKeyFromSender({ key_ct_b64, key_iv_b64, myPrivJwk, theirPubJwk }) {
    try {
        const aes = await deriveAesKey(myPrivJwk, theirPubJwk);
        const pt = await crypto.subtle.decrypt(
            { name: "AES-GCM", iv: unb64(key_iv_b64) },
            aes,
            unb64(key_ct_b64),
        );
        return new Uint8Array(pt);
    } catch {
        return null;
    }
}

/**
 * Decrypt a ciphertext blob given the raw symmetric key bytes
 * (path used by group recipients after they've unwrapped K).
 */
export async function decryptBlobWithRawKey({ ciphertext_b64, iv_b64, rawKey }) {
    try {
        const aes = await crypto.subtle.importKey(
            "raw", rawKey, { name: "AES-GCM" }, false, ["decrypt"],
        );
        const pt = await crypto.subtle.decrypt(
            { name: "AES-GCM", iv: unb64(iv_b64) },
            aes,
            unb64(ciphertext_b64),
        );
        return new Uint8Array(pt);
    } catch {
        return null;
    }
}
